import type { OrderBy } from '@/lib/graphql-query-builder';
import { dateOnlyOf, orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';

/**
 * Scope note: reuses the same question-rendering approach as
 * hr-my-submissions (performance reviews) -- confirmed the Angular source
 * shares the identical question engine between the two. Adds deadline
 * enforcement (client-side date compare, matching Angular's
 * `_checkDeadline`), which reviews don't have. PDF export is deferred (but
 * SurveyDescription is fetched now since it is needed for that later PDF
 * work).
 *
 * The underlying SurveyForm may be sectioned (HasSections + SectionsJson) --
 * see getFormQuestions() below, which flattens section questions so a
 * sectioned survey still renders answerable questions here. Full
 * section-grouped rendering (headings/section descriptions) is deferred;
 * this module answers all questions in a single flat list.
 */
type QuestionFormatType =
  | 'short-text'
  | 'long-text'
  | 'multiple-choice'
  | 'checkboxes'
  | 'rating'
  | 'date'
  | 'likert-scale'
  | 'nps';

interface QuestionOption {
  Label: string;
  Value: string;
}

export interface Question {
  QuestionId: string;
  FormatType: QuestionFormatType;
  Question: string;
  QuestionDescription?: string;
  IsRequired: boolean;
  HasComment: boolean;
  Options?: QuestionOption[];
  MinRating?: number;
  MaxRating?: number;
  LikertLabels?: string[];
  LikertStatements?: string[];
}

type SubmissionStatus = 'Pending' | 'Completed';

export interface SurveySubmission {
  ItemId: string;
  SurveyBatchId: string;
  SurveyFormId: string;
  SurveyTitle?: string;
  SurveyDescription?: string;
  UserId?: string;
  UserName?: string;
  UserEmail?: string;
  /** Kind A: deadline day, stored as `YYYY-MM-DDT00:00:00.000Z`. */
  Deadline?: string;
  /** Kind C: when the survey was requested (UTC). */
  RequestedDateUtc?: string;
  /** Kind C: when the recipient submitted (UTC). */
  SubmittedDateUtc?: string;
  /** IANA zone of the organization the submission was made in. */
  Timezone?: string;
  Status: SubmissionStatus;
  AnswersJson?: string;
}

export interface SurveySubmissionsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: SurveySubmission[];
}

export interface GetSurveySubmissionsParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to latest change first. */
  order?: OrderBy[];
}

export interface AnswerValue {
  QuestionId: string;
  Answer?: string;
  Rating?: number;
  McOptionsValue?: string;
  CbOptionsValues?: string[];
  LikertStatementValues?: Record<string, string>;
  Comment?: string;
}

interface SurveySection {
  SectionId: string;
  Title: string;
  Description?: string;
  Position: number;
  Questions: Question[];
}

export interface SurveyFormLookup {
  ItemId: string;
  Title: string;
  HasSections: boolean;
  QuestionsJson: string;
  SectionsJson?: string;
}

/**
 * Flattens a survey form's questions regardless of whether it is a flat
 * form (QuestionsJson) or a sectioned form (SectionsJson) -- sections are
 * ordered by Position, and questions within each section keep their own
 * Position. This is a deliberate scope cut: section headings/descriptions
 * are not rendered, only the flattened question list.
 */
export const getFormQuestions = (
  form?: Pick<SurveyFormLookup, 'HasSections' | 'QuestionsJson' | 'SectionsJson'>
): Question[] => {
  if (!form) return [];
  if (form.HasSections) {
    try {
      const sections = JSON.parse(form.SectionsJson ?? '[]') as SurveySection[];
      return [...sections]
        .sort((a, b) => a.Position - b.Position)
        .flatMap((section) => section.Questions ?? []);
    } catch {
      return [];
    }
  }
  try {
    return JSON.parse(form.QuestionsJson) as Question[];
  } catch {
    return [];
  }
};

/**
 * The deadline is a date-only value (kind A): its day is compared with the employee's local today in the
 * organization's zone, and the deadline day itself is still open.
 */
export const isDeadlineExpired = (
  deadline?: string,
  now: Date = new Date(),
  timeZone: string = getOrganizationTimeZone()
): boolean => {
  const day = dateOnlyOf(deadline, timeZone);
  if (!day) return false;
  return orgCalendarDate(now.toISOString(), timeZone) > day;
};
